// frontend/src/pages/Services/ExpiringTab.jsx
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Table, Tag, Typography, Select, Space, Button, Card } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { servicesAPI } from '../../api/client';
import { formatDate, BILLING_LABELS, PAYMENT_METHOD_LABELS, CURRENCY_SYMBOLS } from './constants';
import TableToolbar from '../../components/TableToolbar';
import useTableSort from '../../hooks/useTableSort';
import { sortTableRows } from '../../utils/tableRows';
import { makeTablePagination, normalizeTablePagination } from '../../utils/tablePagination';

const { Text } = Typography;

export default function ExpiringTab() {
  const [days, setDays] = useState(60);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'expires_at',
    initialSortDir: 'asc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['services-expiring', days],
    queryFn:  () => servicesAPI.getExpiring(days).then(r => r.data),
  });

  const visibleRows = useMemo(() => sortTableRows(data || [], {
    sortBy,
    sortDir,
    accessors: {
      group_name: row => row.name || 'Основная',
      licenses: row => Number(row.used_licenses || 0),
      cost: row => Number(row.price_per_license || 0) * Number(row.total_licenses || 0),
    },
    types: {
      licenses: 'number',
      cost: 'number',
      expires_at: 'date',
      auto_renew: 'boolean',
    },
  }), [data, sortBy, sortDir]);

  const columns = [
    {
      title: `Сервис${sortIcon('service_name')}`, dataIndex: 'service_name', key: 'service', width: 180,
      onHeaderCell: () => getSortableHeaderProps('service_name'),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Tag color={r.category_color} style={{ fontSize: 10 }}>{r.category_name}</Tag>
        </Space>
      ),
    },
    {
      title: `Группа${sortIcon('group_name')}`, dataIndex: 'name', key: 'name', width: 160,
      onHeaderCell: () => getSortableHeaderProps('group_name'),
      render: (v) => v || 'Основная',
    },
    {
      title: `Лицензий${sortIcon('licenses')}`, key: 'licenses', width: 110, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('licenses'),
      render: (_, r) => (
        <Text>{r.used_licenses} / {r.total_licenses ?? '∞'}</Text>
      ),
    },
    {
      title: `Стоимость${sortIcon('cost')}`, key: 'cost', width: 140,
      onHeaderCell: () => getSortableHeaderProps('cost'),
      render: (_, r) => {
        if (!r.price_per_license || !r.total_licenses) return '—';
        const sum = Number(r.price_per_license) * Number(r.total_licenses);
        const sym = CURRENCY_SYMBOLS[r.currency] || r.currency;
        return <Text>{sum.toLocaleString('ru-RU')} {sym}</Text>;
      },
    },
    {
      title: `Оплата${sortIcon('payment_method')}`, dataIndex: 'payment_method', key: 'payment', width: 90,
      onHeaderCell: () => getSortableHeaderProps('payment_method'),
      render: (v) => v ? <Tag>{PAYMENT_METHOD_LABELS[v]}</Tag> : '—',
    },
    {
      title: `Период${sortIcon('billing_period')}`, dataIndex: 'billing_period', key: 'billing', width: 100,
      onHeaderCell: () => getSortableHeaderProps('billing_period'),
      render: (v) => v ? <Tag>{BILLING_LABELS[v]}</Tag> : '—',
    },
    {
      title: `Истекает${sortIcon('expires_at')}`, dataIndex: 'expires_at', key: 'expires', width: 120,
      onHeaderCell: () => getSortableHeaderProps('expires_at'),
      render: (v, r) => {
        const daysLeft = Number(r.days_left);
        return (
          <Tag color={daysLeft <= 14 ? 'red' : daysLeft <= 30 ? 'orange' : 'gold'}>
            {daysLeft <= 14 && <WarningOutlined style={{ marginRight: 4 }} />}
            {formatDate(v)} ({daysLeft} дн.)
          </Tag>
        );
      },
    },
    {
      title: `Автопрод.${sortIcon('auto_renew')}`, dataIndex: 'auto_renew', key: 'auto', width: 100, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('auto_renew'),
      render: (v) => v ? <Tag color="green">Да</Tag> : <Tag color="default">Нет</Tag>,
    },
  ];

  return (
    <div>
      <TableToolbar>
        <Space wrap size={8}>
          <Select
            value={days}
            onChange={value => { setDays(value); setPagination(p => ({ ...p, current: 1 })); }}
            style={{ width: 160 }}
            options={[
              { label: 'Ближайшие 30 дней',  value: 30  },
              { label: 'Ближайшие 60 дней',  value: 60  },
              { label: 'Ближайшие 90 дней',  value: 90  },
              { label: 'Ближайшие 6 месяцев', value: 180 },
            ]}
          />
          {days !== 60 && <Button onClick={() => { setDays(60); setPagination(p => ({ ...p, current: 1 })); }}>Сбросить</Button>}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Обновить</Button>
        </Space>
      </TableToolbar>

      <Card size="small">
        <Table
          dataSource={visibleRows} columns={columns}
          rowKey="id" loading={isLoading} size="small" scroll={{ x: 1050 }}
          pagination={makeTablePagination({
            ...pagination,
            total: visibleRows.length,
            showTotal: t => `${t} групп`,
          })}
          onChange={(pag) => setPagination(normalizeTablePagination(pag))}
          rowClassName={(r) => Number(r.days_left) <= 14 ? 'row-expiring-critical' : ''}
        />
      </Card>
    </div>
  );
}
